import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { TrendingUp, DollarSign, AlertCircle, Clock } from "lucide-react";

const PipelineView = () => {
  const { userId } = useParams();
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);

  const stages = [
    { id: "qualified", name: "Qualified", color: "bg-blue-500" },
    { id: "discovery", name: "Discovery", color: "bg-purple-500" },
    { id: "proposal", name: "Proposal", color: "bg-yellow-500" },
    { id: "negotiation", name: "Negotiation", color: "bg-orange-500" },
    { id: "closed-won", name: "Closed Won", color: "bg-green-500" }
  ];

  useEffect(() => {
    fetchPipeline();
  }, [userId]);

  const fetchPipeline = async () => {
    try {
      const response = await axios.get(`${API}/pipeline/${userId}`);
      setPipeline(response.data);
      calculateMetrics(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching pipeline:", error);
      toast.error("Failed to load pipeline");
      setLoading(false);
    }
  };

  const calculateMetrics = (pipelineData) => {
    const totalValue = pipelineData.reduce((sum, opp) => sum + opp.value, 0);
    const weightedValue = pipelineData.reduce((sum, opp) => sum + (opp.value * (opp.probability / 100)), 0);
    const dealsAtRisk = pipelineData.filter(opp => {
      const daysSinceUpdate = Math.floor((new Date() - new Date(opp.lastUpdated)) / (1000 * 60 * 60 * 24));
      return daysSinceUpdate > 14 && opp.stage !== "closed-won";
    }).length;

    setMetrics({
      totalValue,
      weightedValue,
      dealsAtRisk,
      dealCount: pipelineData.length
    });
  };

  const getOpportunitiesByStage = (stageId) => {
    return pipeline.filter(opp => opp.stage === stageId);
  };

  const handleCardClick = (opportunity) => {
    toast.info(`Opening deal: $${opportunity.value.toLocaleString()} - ${opportunity.probability}% probability`);
  };

  const getDaysInStage = (lastUpdated) => {
    return Math.floor((new Date() - new Date(lastUpdated)) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#3dd9c5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pipeline-view">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <TrendingUp className="h-8 w-8 text-[#3dd9c5]" />
          Pipeline
        </h1>
        <p className="text-gray-400">Track and manage your sales opportunities</p>
      </div>

      {/* Metrics */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-[#0f1419] border-gray-800/50 p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-400">Total Pipeline</p>
              <DollarSign className="h-5 w-5 text-[#3dd9c5]" />
            </div>
            <p className="text-2xl font-bold text-white">${(metrics.totalValue / 1000).toFixed(0)}K</p>
          </Card>
          <Card className="bg-[#0f1419] border-gray-800/50 p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-400">Weighted Value</p>
              <TrendingUp className="h-5 w-5 text-[#3dd9c5]" />
            </div>
            <p className="text-2xl font-bold text-white">${(metrics.weightedValue / 1000).toFixed(0)}K</p>
          </Card>
          <Card className="bg-[#0f1419] border-gray-800/50 p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-400">Deals at Risk</p>
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <p className="text-2xl font-bold text-red-400">{metrics.dealsAtRisk}</p>
          </Card>
          <Card className="bg-[#0f1419] border-gray-800/50 p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-400">Total Deals</p>
              <Clock className="h-5 w-5 text-[#3dd9c5]" />
            </div>
            <p className="text-2xl font-bold text-white">{metrics.dealCount}</p>
          </Card>
        </div>
      )}

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map((stage) => {
          const opportunities = getOpportunitiesByStage(stage.id);
          const stageValue = opportunities.reduce((sum, opp) => sum + opp.value, 0);

          return (
            <div key={stage.id} className="flex-shrink-0 w-80" data-testid={`stage-${stage.id}`}>
              <Card className="bg-[#0f1419] border-gray-800/50 h-full">
                <div className={`${stage.color} p-4 rounded-t-lg`}>
                  <h3 className="text-white font-semibold">{stage.name}</h3>
                  <p className="text-white/80 text-sm">${(stageValue / 1000).toFixed(0)}K ({opportunities.length})</p>
                </div>
                <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto">
                  {opportunities.map((opp, idx) => (
                    <div
                      key={opp.opportunityId}
                      onClick={() => handleCardClick(opp)}
                      className="bg-white/5 border border-gray-700/50 rounded-lg p-4 cursor-pointer hover:bg-white/10 transition-all"
                      data-testid={`deal-card-${idx}`}
                    >
                      <p className="text-white font-semibold mb-2 text-sm">Deal #{idx + 1}</p>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[#3dd9c5] font-bold">${(opp.value / 1000).toFixed(0)}K</p>
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50 text-xs">
                          {opp.probability}%
                        </Badge>
                      </div>
                      <div className="flex items-center text-xs text-gray-400">
                        <Clock className="h-3 w-3 mr-1" />
                        {getDaysInStage(opp.lastUpdated)} days in stage
                      </div>
                    </div>
                  ))}
                  {opportunities.length === 0 && (
                    <p className="text-gray-500 text-sm text-center py-8">No deals in this stage</p>
                  )}
                </div>
              </Card>
            </div>
          );
        })}
      </div>

      {/* AI Recommendations */}
      <Card className="bg-[#0f1419] border-gray-800/50 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">AI Recommendations</h3>
        <div className="space-y-3">
          <div className="bg-[#3dd9c5]/10 border border-[#3dd9c5]/30 rounded-lg p-4">
            <p className="text-sm text-gray-300">
              <span className="font-semibold text-[#3dd9c5]">High Priority:</span> You have {metrics?.dealsAtRisk || 0} deals at risk that haven't been updated in over 14 days. 
              Focus on moving these forward to prevent pipeline stagnation.
            </p>
          </div>
          <div className="bg-[#3dd9c5]/10 border border-[#3dd9c5]/30 rounded-lg p-4">
            <p className="text-sm text-gray-300">
              <span className="font-semibold text-[#3dd9c5]">Strategy:</span> Your weighted pipeline is ${((metrics?.weightedValue || 0) / 1000).toFixed(0)}K. 
              To hit your goal, focus on accelerating high-probability deals in the Negotiation stage.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PipelineView;
